/**
 * @typedef {import('../types/KeyedObject').default} KeyedObject
 */

const {
	convertCamelCaseToSnakeCase,
	convertSnakeCaseToCamelCase,
	isNumeric,
} = require('./string');

const {
	diff: arrayDiff,
	intersect: arrayIntersect,
	wrap: arrayWrap,
} = require('./array');
const { isEmpty } = require('./common');

/**
 * Check if all nested properties are equal.
 * @param {KeyedObject} objectA
 * @param {KeyedObject} objectB
 * @returns {boolean}
 */
function areDeeplyEqual(objectA, objectB) {
	/**
	 * @function
	 * @param {string|number} key
	 * @returns {boolean}
	 */
	function isEqualProperty(key) {
		return areEqualValues(objectA[key], objectB[key]);
	}

	return (
		Object.keys(objectA).every(isEqualProperty) &&
		Object.keys(objectB).every(isEqualProperty)
	);
}

/**
 * Subroutine for areDeeplyEqual(objectA, objectB).
 * @param {unknown} valueA
 * @param {unknown} valueB
 * @returns {boolean}
 */
function areEqualValues(valueA, valueB) {
	if (
		(isObject(valueA) && isObject(valueB)) ||
		(Array.isArray(valueA) && Array.isArray(valueB))
	) {
		// @ts-ignore nonassignable error that `isObject` should handle
		return areDeeplyEqual(valueA, valueB);
	}

	return valueA === valueB;
}

/**
 * @template {KeyedObject} Type
 * @param {Type} object
 * @returns {Type}
 */
function clone(object) {
	return JSON.parse(JSON.stringify(object));
}

/**
 * Implementation of PHP's `array_column()` function
 * @see {@link https://www.php.net/manual/en/function.array-column.php | PHP function documentation reference}
 * @template {KeyedObject} Type
 * @template {string | number} ObjKey
 * @template {ObjKey} ObjIndex
 * @param {Type[]} array
 * @param {ObjKey} key
 * @param {ObjIndex} [index]
 * @returns {Type[ObjKey][] | { [Key in Type[ObjIndex]]: Type[ObjKey] }[]}
 */
function column(array, key, index) {
	if (!index) {
		return array.map(
			/**
			 * @param {Type} object
			 * @returns {Type[ObjKey]}
			 */
			(object) => object[key]
		);
	}

	return array.map(
		/**
		 * @param {Type} object
		 * @returns {{ [Key in ObjIndex]: Type[ObjKey] }}
		 */
		// @ts-ignore `index` is an key of `object`.
		(object) => ({ [object[index]]: object[key] })
	);
}

/**
 * Convert all object keys in place from camelCase to snake_case.
 * @param {KeyedObject} object
 * @returns {void}
 */
function convertCamelCaseKeysToSnakeCase(object) {
	Object.entries(object).forEach(([key, value]) => {
		const snakeCasedKey =
			typeof key === 'string' && !isNumeric(key)
				? convertCamelCaseToSnakeCase(key)
				: key;

		if (snakeCasedKey !== key) {
			/* eslint-disable no-param-reassign */
			object[snakeCasedKey] = value;
			delete object[key];
			/* eslint-enable no-param-reassign */
		}

		if (typeof value === 'object' && value !== null) {
			convertCamelCaseKeysToSnakeCase(value);
		}
	});
}

/**
 * Convert all object keys in place from camelCase to snake_case.
 * @param {KeyedObject} object
 * @returns {void}
 */
function convertSnakeCaseKeysToCamelCase(object) {
	Object.entries(object).forEach(([key, value]) => {
		const camelCasedKey =
			typeof key === 'string' && !isNumeric(key) && key.includes('_')
				? convertSnakeCaseToCamelCase(key)
				: key;

		if (camelCasedKey !== key) {
			/* eslint-disable no-param-reassign */
			object[camelCasedKey] = value;
			delete object[key];
			/* eslint-enable no-param-reassign */
		}

		if (typeof value === 'object' && value !== null) {
			convertSnakeCaseKeysToCamelCase(value);
		}
	});
}

/**
 * Freezes the object and all nested objects.
 * @template {KeyedObject} Type
 * @param {Type} object
 * @returns {Type}
 */
function deepFreeze(object) {
	Object.getOwnPropertyNames(object).forEach((name) => {
		const value = object[name];

		if (typeof value === 'object' && value !== null) {
			deepFreeze(value);
		}
	});

	return Object.freeze(object);
}

/**
 * @template {KeyedObject} ObjectA
 * @template {KeyedObject} ObjectB
 * @param {ObjectA} a
 * @param {ObjectB} b
 * @returns {Omit<ObjectA, keyof ObjectB>}
 */
function diffKeys(a, b) {
	const keyList = arrayDiff(getKeys(a), getKeys(b));

	return only(a, ...keyList);
}

/**
 * @template {KeyedObject} Dictionary
 * @template {string | number} Dots
 * @template {any} Fallback
 * @param {Dictionary} object
 * @param {Dots} dots
 * @param {Fallback | null} [defaultValue]
 * @returns {Dictionary | Dictionary[Dots] | Fallback | null}
 */
function get(object, dots, defaultValue = null) {
	if (!dots) {
		return object;
	}

	if (Number.isInteger(dots)) {
		return object[dots] || defaultValue;
	}

	// @ts-ignore throws non-existent `number.includes()` error that `Number.isInteger` takes care of
	if (!dots.includes('.')) {
		return object[dots] || defaultValue;
	}

	// @ts-ignore throws non-existent `number.split()` error that `Number.isInteger` takes care of
	const keyList = dots.split('.');
	const key = keyList.shift();

	if (object[key] === undefined) {
		return null;
	}

	return get(object[key], keyList.join('.'), defaultValue);
}

/**
 * Returns a strictly-typed array of key/value pairs of the enumerable
 * properties of an object.
 *
 * Use Object.entries() instead if it is possible that the object has
 * additional properties that are not declared on its type.
 * @template {KeyedObject} Type
 * @param {Type} object
 * @returns {{[Key in keyof Type]: [`${Key}`, Type[Key]]}[keyof Type][]}
 */
function getEntries(object) {
	// @ts-ignore This narrowing conversion works if you are certain there are no additional properties.
	return Object.entries(object);
}

/**
 * Returns the strictly-typed names of the enumerable string properties and
 * methods of an object.
 *
 * Use Object.keys() instead if it is possible that the object has
 * additional properties that are not declared on its type.
 * @template {KeyedObject} Type
 * @param {Type} object
 * @returns {(keyof Type)[]}
 */
function getKeys(object) {
	// @ts-ignore This narrowing conversion works if you are certain there are no additional properties.
	return Object.keys(object);
}

/**
 * Returns a strictly-typed array of values of the enumerable properties
 * of an object.
 *
 * Use Object.values() instead if it is possible that the object has
 * additional properties that are not declared on its type.
 * @template {KeyedObject} Type
 * @param {Type} object
 * @returns {Type[keyof Type][]}
 */
function getValues(object) {
	return Object.values(object);
}

/**
 * @template {KeyedObject} Type
 * @template {keyof Type} TypeKey
 * @param {Type[]} array
 * @param {TypeKey} key
 * @returns {{ [Key in Type[TypeKey]]: Type[] }}
 */
function groupBy(array, key) {
	return array.reduce(
		/**
		 *
		 * @param {KeyedObject} result
		 * @param {KeyedObject} current
		 * @returns {KeyedObject}
		 */
		(result, current) => {
			// @ts-ignore `TypeKey` is defined above as a `keyof Type`
			const value = current[key];
			/* eslint-disable-next-line no-param-reassign */
			result[value] = result[value] || [];
			result[value].push(current);

			return result;
		},
		{}
	);
}

/**
 * @param {KeyedObject} object
 * @param  {...(string|number)} keys
 * @returns {boolean}
 */
function includesAll(object, ...keys) {
	return keys.every(
		/**
		 * @param {string|number} key
		 * @returns {boolean}
		 */
		(key) => object[key] !== undefined
	);
}

/**
 * @param {KeyedObject} object
 * @param  {...(string|number)} keys
 * @returns {boolean}
 */
function includesAllWithValues(object, ...keys) {
	return keys.every(
		/**
		 * @param {string|number} key
		 * @returns {boolean}
		 */
		(key) => !isEmpty(object[key])
	);
}

/**
 * @param {KeyedObject} object
 * @param  {...(string|number)} keys
 * @returns {boolean}
 */
function includesAny(object, ...keys) {
	return keys.some(
		/**
		 * @param {string|number} key
		 * @returns {boolean}
		 */
		(key) => object[key] !== undefined
	);
}

/**
 * @param {KeyedObject} object
 * @param  {...(string|number)} keys
 * @returns {boolean}
 */
function includesAnyWithValues(object, ...keys) {
	return keys.some((key) => !isEmpty(object[key]));
}

/**
 * @template {KeyedObject} ObjectA
 * @template {KeyedObject} ObjectB
 * @param {ObjectA} a
 * @param {ObjectB} b
 * @returns {Record<keyof ObjectB, ObjectA>}
 */
function intersectKeys(a, b) {
	const keyList = arrayIntersect(getKeys(a), getKeys(b));

	// @ts-ignore, omits keys that are not present.
	return only(a, ...keyList);
}

/**
 * Check if value is a non-null, non-array object.
 * @param {unknown} value
 * @returns {value is KeyedObject}
 */
function isObject(value) {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Returns the object with only the specified keys
 * @todo Add deeply nested keys
 * @template {KeyedObject} Type
 * @template {keyof Type} TypeKey
 * @template {Pick<Type, TypeKey>} ReturnValue
 * @param {Type} object
 * @param  {...TypeKey} keyList
 * @returns {ReturnValue}
 */
function only(object, ...keyList) {
	/** @type {ReturnValue} */
	// @ts-ignore, values will be assigned
	const newObject = {};

	keyList.forEach((key) => {
		if (key in object) {
			// @ts-ignore `key` is verified above
			newObject[key] = object[key];
		}
	});

	return newObject;
}

/**
 * @template {KeyedObject} Type
 * @template {string | number} Dots
 * @template {any} Value
 * @param {Type} object
 * @param {Dots} dots
 * @param {Value} value
 * @returns {Type & { [Key in Dots]: Value }}
 */
function set(object, dots, value) {
	if (!dots) {
		return object;
	}

	const modified = clone(object);

	// @ts-ignore throws non-existent `number.includes()` error that `Number.isInteger` takes care of
	if (Number.isInteger(dots) || !dots.includes('.')) {
		// @ts-ignore Anything is assignable to this key in this instance
		modified[dots] = value;

		return modified;
	}

	// @ts-ignore throws non-existent `number.includes()` error that `Number.isInteger` takes care of
	const keyList = dots.split('.');
	const key = keyList.shift();
	modified[key] = set(modified[key] || {}, keyList.join('.'), value);

	return modified;
}

/**
 * Unwind nested objects.
 * @param {KeyedObject} object
 * @param {'camelCase'|'snake_case'} keyCasing
 * @param {string=} keyPrefix
 * @returns {KeyedObject}
 */
function unwind(object, keyCasing, keyPrefix) {
	/** @type {KeyedObject} */
	const newObject = {};

	getEntries(object).forEach(([key, value]) => {
		const computedKey = keyPrefix
			? `${keyPrefix}${computeKeySegment(key, keyCasing)}`
			: key;

		if (isObject(value)) {
			Object.assign(newObject, unwind(value, keyCasing, computedKey));
		} else {
			newObject[computedKey] = value;
		}
	});

	return newObject;
}

/**
 * Subroutine of unwind(object, keyCasing, keyPrefix).
 * @param {string} key
 * @param {'camelCase'|'snake_case'} casing
 * @returns {string}
 * @throws {Error}
 */
function computeKeySegment(key, casing) {
	switch (casing) {
		case 'camelCase':
			return `${key[0].toUpperCase()}${key.slice(1)}`;
		case 'snake_case':
			return `_${key}`;
		default:
			throw new Error(
				`${casing} is an invalid argument for "keyCasing" in unwind(object, keyCasing, keyPrefix).`
			);
	}
}

/**
 * Returns the object without the specified keys
 * @template {KeyedObject} Type
 * @template {keyof Type} TypeKey
 * @template {Omit<Type, TypeKey>} ReturnValue
 * @param {Type} object
 * @param  {...TypeKey} keyList
 * @returns {ReturnValue}
 */
function without(object, ...keyList) {
	/** @type {ReturnValue} */
	// @ts-ignore Values will be set below
	const newObject = {};

	getKeys(object).forEach((key) => {
		// @ts-ignore `keyof Type` is a key of `Type`.
		if (keyList.includes(key)) {
			return;
		}

		// @ts-ignore `keyList` are the assignable type keys.
		newObject[key] = object[key];
	});

	return newObject;
}

/**
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value#circular_references | Refer to MDN documentation}
 * @returns {Function}
 */
function removeCircularReferences() {
	const seen = new WeakSet();

	/**
	 * @function
	 * @param {string|number} key
	 * @param {unknown} value
	 * @returns {unknown}
	 */
	return (key, value) => {
		/* eslint-disable consistent-return */
		if (typeof value === 'object' && value !== null) {
			if (seen.has(value)) {
				return;
			}

			seen.add(value);
		}

		return value;
		/* eslint-enable consistent-return */
	};
}

/**
 * @param {KeyedObject} object
 * @returns {string}
 */
function toJson(object) {
	return JSON.stringify(clone(object), removeCircularReferences);
}

/**
 * @template {KeyedObject} Type
 * @template {keyof Type} FromKeys
 * @template {string} ToKeys
 * @template {{ [Key in ToKeys]: Type[FromKeys] }} ReturnValue
 * @param {Type} object
 * @param {Map<ToKeys, FromKeys>} map
 * @param {((value: any) => any)=} callback
 * @returns {ReturnValue}
 */
function transform(object, map, callback = undefined) {
	/** @type {ReturnValue} */
	// @ts-ignore, assigned below
	let data = {};

	map.forEach((from, to) => {
		// @ts-ignore `from` and `to` will be strings
		const value = get(object, from);
		data = set(data, to, callback ? callback(value) : value);
	});

	return data;
}

/**
 * @private
 * @template {any} Value
 * @param {Value} a
 * @param {Value} b
 * @returns {any}
 */
function mergeValues(a, b) {
	if (areEqualValues(a, b)) {
		return a;
	}

	if (a === undefined || a === null) {
		return b;
	}

	if (Array.isArray(a) || Array.isArray(b)) {
		return Array.from(new Set([...arrayWrap(a), ...arrayWrap(b)]));
	}

	if (['boolean', 'number', 'string'].includes(typeof a)) {
		return a;
	}

	// @ts-ignore, `a` cannot be `unknown`
	return merge(a, b);
}

/**
 * Merges two or more keyed objects together using the left most objects as the "source of truth" for duplicate keys
 * Note: undefined and null values will be overwritten by the next item on the right
 * Note: this will not copy non-iterable object properties
 * @todo This type could be much better flushed out
 * @template {KeyedObject} Value
 * @param  {...Value} objectList
 * @returns {Value | null}
 */
function merge(...objectList) {
	if (objectList.length === 0) {
		return null;
	}

	const first = objectList.find(Boolean);

	if (objectList.length === 1) {
		return first === undefined ? null : first;
	}

	let equal = true;

	// Loop through all items for equality
	/* eslint-disable-next-line no-restricted-syntax */
	for (const key of objectList.keys()) {
		const nextKey = key + 1 > objectList.length - 1 ? 0 : key + 1;
		equal = areEqualValues(objectList[key], objectList[nextKey]);

		if (!equal) {
			// Stop loop if not equal
			break;
		}
	}

	// If they're all the same, return the first
	if (equal) {
		return first === undefined ? null : first;
	}

	/** @type {Value} */
	// @ts-ignore
	const destination = {};

	objectList.forEach((object) => {
		Object.entries(object).forEach(
			/**
			 * @param {[keyof Value, Value[key]]} param
			 * @returns {void}
			 */
			// @ts-ignore
			([key, value]) => {
				destination[key] = mergeValues(
					destination[key] ? null : value,
					value
				);
			}
		);
	});

	return destination;
}

module.exports = {
	areDeeplyEqual,
	clone,
	column,
	convertCamelCaseKeysToSnakeCase,
	convertSnakeCaseKeysToCamelCase,
	deepFreeze,
	diffKeys,
	get,
	getEntries,
	getKeys,
	getValues,
	groupBy,
	includesAll,
	includesAllWithValues,
	includesAny,
	includesAnyWithValues,
	intersectKeys,
	isObject,
	merge,
	only,
	set,
	toJson,
	transform,
	unwind,
	without,
};
